import React, { Component } from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography, Chip, Skeleton, TextField, IconButton, Tooltip, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { toCsv, downloadCsv } from '../usage/usage-csv';
import ReportScheduleService, { getCatalogEntry } from '../../services/report-schedule.service';

// Detalhe de UMA execução (agendada, manual ou teste): cabeçalho, erro completo e status individual de cada destinatário.
// Atualiza sozinho a cada 5 s enquanto o run ou algum destinatário estiver pendente (até ~5 min).
const RUN_STATUS_STYLE = {
  queued: { bg: '#e8f1ff', color: '#277afe' },
  processing: { bg: '#e8f1ff', color: '#277afe' },
  delivered: { bg: '#eef7e3', color: '#5b9b1e' },
  failed: { bg: '#fdecea', color: '#c62828' },
  skipped_empty: { bg: '#f1f1f1', color: '#777' },
};
const RECIPIENT_STATUS = {
  queued: { bg: '#e8f1ff', color: '#277afe', icon: <HourglassEmptyOutlinedIcon /> },
  sent: { bg: '#eef7e3', color: '#5b9b1e', icon: <MarkEmailReadOutlinedIcon /> },
  failed: { bg: '#fdecea', color: '#c62828', icon: <ErrorOutlineIcon /> },
  discarded: { bg: '#fff4e5', color: '#b9770e', icon: <BlockOutlinedIcon /> },
};
const POLL_MS = 5000;
const MAX_POLLS = 60;

class RunDetailsDialog extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, refreshing: false, run: null, error: '', search: '', statusFilter: 'all' };
    this.timer = null;
    this.polls = 0;
    this.unmounted = false;
  }

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.timer) clearTimeout(this.timer);
  }

  load = () => {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    this.setState({ refreshing: true });
    ReportScheduleService.getRunDetail(this.props.runId)
      .then(result => {
        if (this.unmounted) return;
        const run = (result.data && result.data.success) ? result.data.data : null;
        this.setState({ loading: false, refreshing: false, run, error: '' });
        if (run && this.isPending(run) && this.polls < MAX_POLLS) {
          this.polls += 1;
          this.timer = setTimeout(this.load, POLL_MS);
        }
      })
      .catch(err => {
        if (this.unmounted) return;
        const msg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0])
          || this.props.intl.formatMessage({ id: 'report.schedule.toast.error' });
        this.setState({ loading: false, refreshing: false, error: msg });
      });
  };

  refresh = () => { this.polls = 0; this.load(); };

  isPending(run) {
    return run.status === 'queued' || run.status === 'processing' || run.recipients.some(r => r.status === 'queued');
  }

  fmt = (d, f = 'DD/MM/YYYY HH:mm:ss') => (d ? moment(d).format(f) : '—');

  // Execuções anteriores ao detalhamento só têm os descartados no JSON do run.
  recipientsOf(run) {
    if (run.recipients.length > 0) return run.recipients;
    return run.discarded.map((d, i) => ({ id: `legacy-${i}`, type: '', name: d.target, email: '', status: 'discarded', discardReason: d.reason, error: '', sentAt: null, attempts: 0 }));
  }

  // CSV com TODOS os destinatários do run (ignora busca/filtro da tela). Gerado no navegador a partir do run carregado.
  exportCsv = () => {
    const { intl } = this.props;
    const { run } = this.state;
    if (!run) return;
    const t = (id) => intl.formatMessage({ id });
    const columns = [
      { header: t('report.schedule.rundetail.csv.name'), value: r => r.name },
      { header: t('report.schedule.rundetail.csv.email'), value: r => r.email },
      { header: t('report.schedule.rundetail.col.source'), value: r => (r.type ? t(r.type === 'owner' ? 'report.schedule.rundetail.owner' : `report.schedule.recipientType.${r.type}`) : '') },
      { header: t('report.schedule.rundetail.col.status'), value: r => t(`report.schedule.rundetail.rstatus.${r.status}`) },
      { header: t('report.schedule.rundetail.col.sentAt'), value: r => (r.sentAt ? moment(r.sentAt).format('DD/MM/YYYY HH:mm:ss') : '') },
      { header: t('report.schedule.rundetail.csv.attempts'), value: r => r.attempts || 0 },
      { header: t('report.schedule.rundetail.col.detail'), value: r => this.recipientDetail(r) },
    ];
    const suffix = run.triggeredBy === 'test' ? 'teste' : `envio-${run.runNumber}`;
    downloadCsv(`destinatarios-${suffix}-${moment().format('YYYYMMDD-HHmmss')}.csv`, toCsv(columns, this.recipientsOf(run)));
  };

  infoItem(labelId, value) {
    const { intl } = this.props;
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3 }}>{intl.formatMessage({ id: labelId })}</Typography>
        <Box sx={{ fontSize: 14, color: '#333', mt: 0.3, wordBreak: 'break-word' }}>{value}</Box>
      </Box>
    );
  }

  kpi(key, icon, n, color) {
    const { intl } = this.props;
    const active = this.state.statusFilter === key;
    return (
      <Box key={key} onClick={() => this.setState({ statusFilter: active ? 'all' : key })} sx={{
        flex: '1 1 120px', p: 1.5, borderRadius: '12px', cursor: 'pointer', backgroundColor: '#fff',
        border: active ? `2px solid ${color}` : '1px solid #e0e0e0', transition: 'border-color .15s', '&:hover': { borderColor: color },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, color, '& svg': { fontSize: 18 } }}>
          {icon}
          <Typography sx={{ fontSize: 12, color: '#777' }}>{intl.formatMessage({ id: `report.schedule.rundetail.kpi.${key}` })}</Typography>
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#333', mt: 0.5 }}>{n}</Typography>
      </Box>
    );
  }

  recipientStatusChip(r) {
    const { intl } = this.props;
    const st = RECIPIENT_STATUS[r.status] || RECIPIENT_STATUS.queued;
    return (
      <Chip size="small" icon={st.icon} label={intl.formatMessage({ id: `report.schedule.rundetail.rstatus.${r.status}` })}
        sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600, '& .MuiChip-icon': { color: st.color, fontSize: 16 } }} />
    );
  }

  recipientDetail(r) {
    const { intl } = this.props;
    if (r.status === 'discarded') return intl.formatMessage({ id: `report.schedule.eligibility.${r.discardReason || 'no_email'}` });
    if (r.status === 'failed') return r.error || intl.formatMessage({ id: 'report.schedule.rundetail.failedNoDetail' });
    if (r.status === 'queued') return intl.formatMessage({ id: 'report.schedule.rundetail.queuedHint' });
    return intl.formatMessage({ id: 'report.schedule.rundetail.sentHint' });
  }

  renderBody() {
    const { intl } = this.props;
    const { run, search, statusFilter } = this.state;
    const entry = getCatalogEntry(run.reportKey);
    const st = RUN_STATUS_STYLE[run.status] || RUN_STATUS_STYLE.queued;
    const all = this.recipientsOf(run);
    const count = (s) => all.filter(r => r.status === s).length;
    const q = search.trim().toLowerCase();
    const visible = all
      .filter(r => statusFilter === 'all' || r.status === statusFilter)
      .filter(r => !q || `${r.name} ${r.email}`.toLowerCase().includes(q));
    const period = run.periodStart
      ? (run.periodEnd && run.periodEnd !== run.periodStart
        ? `${moment(run.periodStart).format('DD/MM/YYYY')} – ${moment(run.periodEnd).format('DD/MM/YYYY')}`
        : moment(run.periodStart).format('DD/MM/YYYY'))
      : '—';
    const file = [run.format ? run.format.toUpperCase() : null,
      run.status === 'delivered' ? intl.formatMessage({ id: `report.schedule.rundetail.delivery.${run.delivery === 'attachment' ? 'attachment' : 'link'}` }) : null]
      .filter(Boolean).join(' · ') || '—';
    const head = { fontSize: 12, fontWeight: 700, color: '#666', backgroundColor: '#fafafa', whiteSpace: 'nowrap' };

    return (
      <Box>
        {run.error && (
          <Alert severity={run.status === 'failed' ? 'error' : 'warning'} sx={{ mb: 2, borderRadius: '10px' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.3 }}>{intl.formatMessage({ id: 'report.schedule.rundetail.error' })}</Typography>
            <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{run.error}</Typography>
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: '12px', backgroundColor: '#fafafa' }}>
          {this.infoItem('report.schedule.rundetail.status',
            <Chip size="small" label={intl.formatMessage({ id: `report.schedule.run.status.${run.status}` })} sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />)}
          {this.infoItem('report.schedule.rundetail.report', entry ? intl.formatMessage({ id: entry.labelId }) : (run.reportKey || '—'))}
          {this.infoItem('report.schedule.rundetail.trigger',
            `${intl.formatMessage({ id: `report.schedule.trigger.${run.triggeredBy}` })}${run.requestedByName && run.triggeredBy !== 'scheduler' ? ` · ${run.requestedByName}` : ''}`)}
          {this.infoItem('report.schedule.rundetail.period', period)}
          {this.infoItem('report.schedule.rundetail.rows', run.rowCount === null || run.rowCount === undefined ? '—' : run.rowCount.toLocaleString())}
          {this.infoItem('report.schedule.rundetail.file', file)}
          {this.infoItem('report.schedule.rundetail.startedAt', this.fmt(run.startedAt || run.scheduledFor))}
          {this.infoItem('report.schedule.rundetail.finishedAt', this.fmt(run.finishedAt))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          {this.kpi('total', <GroupOutlinedIcon />, all.filter(r => r.status !== 'discarded').length, '#555')}
          {this.kpi('sent', RECIPIENT_STATUS.sent.icon, count('sent'), RECIPIENT_STATUS.sent.color)}
          {this.kpi('queued', RECIPIENT_STATUS.queued.icon, count('queued'), RECIPIENT_STATUS.queued.color)}
          {this.kpi('failed', RECIPIENT_STATUS.failed.icon, count('failed'), RECIPIENT_STATUS.failed.color)}
          {this.kpi('discarded', RECIPIENT_STATUS.discarded.icon, count('discarded'), RECIPIENT_STATUS.discarded.color)}
        </Box>

        {all.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, border: '1px dashed #e0e0e0', borderRadius: '12px' }}>
            <GroupOutlinedIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
            <Typography sx={{ fontSize: 14, color: '#888', mt: 1, px: 2 }}>
              {intl.formatMessage({ id: run.status === 'queued' || run.status === 'processing' ? 'report.schedule.rundetail.pendingRecipients'
                : (run.recipientsCount > 0 ? 'report.schedule.rundetail.legacy' : 'report.schedule.rundetail.empty') })}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
              <TextField size="small" sx={{ flex: '1 1 240px' }} placeholder={intl.formatMessage({ id: 'report.schedule.rundetail.search' })}
                value={search} onChange={e => this.setState({ search: e.target.value })} />
              {statusFilter !== 'all' && (
                <Chip size="small" onDelete={() => this.setState({ statusFilter: 'all' })}
                  label={intl.formatMessage({ id: `report.schedule.rundetail.kpi.${statusFilter}` })} />
              )}
            </Box>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={head}>{intl.formatMessage({ id: 'report.schedule.rundetail.col.recipient' })}</TableCell>
                    <TableCell sx={head}>{intl.formatMessage({ id: 'report.schedule.rundetail.col.source' })}</TableCell>
                    <TableCell sx={head}>{intl.formatMessage({ id: 'report.schedule.rundetail.col.status' })}</TableCell>
                    <TableCell sx={head}>{intl.formatMessage({ id: 'report.schedule.rundetail.col.sentAt' })}</TableCell>
                    <TableCell sx={head}>{intl.formatMessage({ id: 'report.schedule.rundetail.col.detail' })}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.length === 0 ? (
                    <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: '#888', fontSize: 13, py: 3 }}>
                      {intl.formatMessage({ id: 'report.schedule.rundetail.noMatch' })}
                    </TableCell></TableRow>
                  ) : visible.map(r => (
                    <TableRow key={r.id}>
                      <TableCell sx={{ fontSize: 13, maxWidth: 260 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{r.name || r.email || '—'}</Typography>
                        {r.email && r.name && <Typography sx={{ fontSize: 12, color: '#888', wordBreak: 'break-all' }}>{r.email}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                        {r.type ? intl.formatMessage({ id: r.type === 'owner' ? 'report.schedule.rundetail.owner' : `report.schedule.recipientType.${r.type}` }) : '—'}
                      </TableCell>
                      <TableCell>{this.recipientStatusChip(r)}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{this.fmt(r.sentAt)}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: r.status === 'failed' ? '#c62828' : '#777', maxWidth: 320, wordBreak: 'break-word' }}>
                        {this.recipientDetail(r)}
                        {r.attempts > 1 && <Typography component="span" sx={{ fontSize: 11, color: '#999', ml: 0.5 }}>· {intl.formatMessage({ id: 'report.schedule.rundetail.attempts' }, { n: r.attempts })}</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    );
  }

  render() {
    const { intl, open, onClose } = this.props;
    const { loading, refreshing, run, error } = this.state;
    const subtitle = run
      ? `${run.name || '—'} · ${run.triggeredBy === 'test' ? intl.formatMessage({ id: 'report.schedule.trigger.test' }) : `#${run.runNumber}`}`
      : '';
    const polling = run && this.isPending(run) && this.polls < MAX_POLLS;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{intl.formatMessage({ id: 'report.schedule.rundetail.title' })}</Typography>
              {subtitle && <Typography sx={{ fontSize: 13, color: '#888' }}>{subtitle}</Typography>}
            </Box>
            <Tooltip title={intl.formatMessage({ id: 'report.schedule.rundetail.refresh' })}>
              <span>
                <IconButton size="small" onClick={this.refresh} disabled={refreshing}>
                  {refreshing && !loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          {polling && (
            <Typography sx={{ fontSize: 12, color: '#277afe', mt: 0.5 }}>{intl.formatMessage({ id: 'report.schedule.rundetail.autoRefresh' })}</Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ maxHeight: '65vh', overflowY: 'auto', padding: '16px 20px' }}>
            {loading ? (
              <Box>
                <Skeleton variant="rounded" height={96} animation="wave" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={72} animation="wave" sx={{ flex: 1 }} />)}</Box>
                {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={42} animation="wave" sx={{ my: 1 }} />)}
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>
            ) : run ? this.renderBody() : null}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={this.exportCsv}
            disabled={loading || !run || this.recipientsOf(run).length === 0}
            sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444', mr: 'auto' }}>
            {intl.formatMessage({ id: 'report.schedule.rundetail.csv.download' })}
          </Button>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>{intl.formatMessage({ id: 'report.schedule.close' })}</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default injectIntl(RunDetailsDialog);
